<template>
  <v-dialog
      v-model="dialog"
      :width="dialogWidth"
      @keydown.enter.prevent="onenter"
  >
    <v-card
        class="resizable-card"
        :class="{ 'no-select': resizing }"
        :style="{ height: dialogHeight + 'px' }"
    >
      <v-card-title>
        <span class="headline">Item Kind ({{ rules.name }})</span>
        <v-spacer/>
        <r-authorize
            :master="masterAccess"
            :locked.sync="model.locked"
            :enabled.sync="model.enabled"
        />
      </v-card-title>
      <v-card-text class="resizable-card__body">
        <v-spacer/>
          <v-btn @click="showPreview()" class="mr-2" small>View JSON</v-btn>
        <v-spacer/>
        <v-row dense>
          <v-col cols="12" md="6" sm="6">
            <v-text-field
                label="Key:"
                v-model.trim="model.key"
                v-ascii-only
            />
          </v-col>
          <v-col cols="12" md="6" sm="6">
            <v-text-field label="Code:" v-model.number="model.code" />
          </v-col>
        </v-row>

        <v-row dense>
          <v-col cols="12" sm="6" md="6">
            <r-item-group-dropdown :model.sync="model.group" />
          </v-col>
          <v-col cols="12" md="6" sm="6">
            <v-text-field label="Priority:" v-model.number="model.priority" />
          </v-col>
        </v-row>

        <v-row dense style="margin-top: -12px">
          <v-col cols="12">
            <r-cell-kinds-select v-model="model.cellKinds" />
          </v-col>
        </v-row>

        <v-row dense>
          <v-col cols="6">
            <v-text-field
                label="Levels:"
                v-model="safeLevels"
                @blur="parseLevels(levelsInput)"
            />
          </v-col>
          <v-col cols="6">
            <r-point :model.sync="model.posHero" label="Position Hero" />
          </v-col>
        </v-row>

        <v-row dense style="margin-top: -12px">
          <v-col cols="6">
            <v-checkbox
                v-model="safeHasMergePrompt"
                label="Has Merge Prompt"
            />
          </v-col>
          <v-col cols="6">
            <v-checkbox
                v-model="safeUseCustomIcon"
                label="Use Custom Icon"
            />
          </v-col>
        </v-row>

        <v-row dense>
          <v-expansion-panels focusable>
            <r-owners-panel :model="model" />
            <r-maps-panel :model="model" />
          </v-expansion-panels>
        </v-row>
      </v-card-text>

      <v-card-actions class="resizable-card__actions">
        <v-spacer/>
        <v-btn @click="cancel" color="blue darken-1" text>
          Cancel
        </v-btn>
        <v-btn @click="save" color="blue darken-1" text v-if="!lockedAssess || masterAccess">
          Save
        </v-btn>
      </v-card-actions>

      <div class="resize-handle" @mousedown="startResize"></div>
      <r-preview-dialog ref="preview-dialog"></r-preview-dialog>
    </v-card>
  </v-dialog>
</template>

<script>
import VueTypes from "vue-types";
import {ViewRules} from "@/common/ViewRules";
import {RedactorUtils} from "@/modules/redactor/core/RedactorUtils";
import {ItemKindModel} from "@/modules/redactor/models/item/core/ItemKindModel";

import RAuthorize from "@/modules/redactor/views/common/r-authorize";
import RItemGroupDropdown from "@/modules/redactor/views/item-group/r-item-group-dropdown.vue";
import RCellKindsSelect from "@/modules/redactor/views/utils/r-cell-kinds-select.vue";
import ROwnersPanel from "@/modules/redactor/views/utils/r-owners-panel.vue";
import RMapsPanel from "@/modules/redactor/views/utils/r-maps-panel.vue";
import RPoint from "@/modules/redactor/views/utils/r-point.vue";
import RPreviewDialog from "@/modules/redactor/views/preview/r-preview-dialog.vue";

export default {
  name: "r-item-kind-dialog",
  components: {
    RPreviewDialog,
    RAuthorize,
    RItemGroupDropdown,
    RCellKindsSelect,
    ROwnersPanel,
    RMapsPanel,
    RPoint,
  },
  data: () => ({
    dialog: false,
    resolve: null,
    reject: null,
    model: VueTypes.objectOf(ItemKindModel),
    rules: ViewRules.READONLY,
    levelsInput: "",
    dialogWidth: 575,
    dialogHeight: 546,
    resizing: false,
    resizeStartX: 0,
    resizeStartY: 0,
    resizeStartWidth: 575,
    resizeStartHeight: 546,
    viewportPadding: 32
  }),
  computed: {
    lockedAssess() {
      return this.model.locked;
    },
    masterAccess() {
      return RedactorUtils.hasMasterAccess();
    },
    safeHasMergePrompt: {
      get() {
        return !!this.model.hasMergePrompt;
      },
      set(value) {
        this.model.hasMergePrompt = !!value;
      }
    },
    safeUseCustomIcon: {
      get() {
        return !!this.model.useCustomIcon;
      },
      set(value) {
        this.model.useCustomIcon = !!value;
      }
    },
    safeLevels: {
      get() {
        if (Array.isArray(this.model.levels)) {
          return this.model.levels.join(", ");
        }
        return "";
      },
      set(val) {
        this.levelsInput = val;
      }
    }
  },
  methods: {
    parseLevels(value) {
      const cleaned = (value || "")
          .replace(/\s+/g, "")
          .replace(/^,|,$/g, "");

      if (/^\d+$/.test(cleaned)) {
        const max = Number(cleaned);
        this.model.levels = Array.from({ length: max + 1 }, (_, i) => i);
        return;
      }

      this.model.levels = cleaned
          .split(",")
          .map(s => parseInt(s, 10))
          .filter(n => Number.isInteger(n) && n >= 0);
    },

    show(rules, model) {
      this.dialog = true;
      this.rules = rules;
      this.model = model || new ItemKindModel();

      if (ViewRules.COPY.equal(this.rules)) {
        this.model.locked = false;
      }

      if (!Array.isArray(this.model.owners)) {
        this.model.owners = [];
      }

      if (!Array.isArray(this.model.maps)) {
        this.model.maps = [];
      }

      if (ViewRules.CREATE.equal(this.rules) || ViewRules.COPY.equal(this.rules)) {
        const allItems = RedactorUtils.getItemKinds();
        const maxCode = allItems.reduce((max, item) =>
            Number.isInteger(item.code) && item.code > max ? item.code : max, -1);
        this.model.code = maxCode + 1;
      }

      return new Promise((resolve, reject) => {
        this.resolve = resolve;
        this.reject = reject;
      });
    },

    save() {
      const allItems = RedactorUtils.getItemKinds();

      const duplicateKey = allItems.find(item =>
          item.key === this.model.key && item.uid !== this.model.uid
      );

      if (duplicateKey) {
        this.$alert("Duplicate key detected!");
        return;
      }

      const duplicateCode = allItems.find(item =>
          item.code === this.model.code && item.uid !== this.model.uid
      );

      if (duplicateCode) {
        this.$alert("Duplicate code detected!");
        return;
      }

      if (this.model.valid()) {
        this.resolve && this.resolve(this.model);
        this.discard();
      } else {
        this.$alert("Invalid config!");
      }
    },

    cancel() {
      this.reject && this.reject();
      this.discard();
    },

    discard() {
      this.dialog = false;
      this.stopResize();
    },

    onenter() {
      if (!this.lockedAssess || this.masterAccess) {
        this.save();
      } else {
        this.cancel();
      }
    },

    startResize(e) {
      this.resizing = true;
      this.resizeStartX = e.clientX;
      this.resizeStartY = e.clientY;
      this.resizeStartWidth = this.dialogWidth;
      this.resizeStartHeight = this.dialogHeight;
      window.addEventListener("mousemove", this.onResize);
      window.addEventListener("mouseup", this.stopResize);
    },

    onResize(e) {
      if (!this.resizing) return;
      const dx = e.clientX - this.resizeStartX;
      const dy = e.clientY - this.resizeStartY;
      let newWidth = this.resizeStartWidth + dx;
      let newHeight = this.resizeStartHeight + dy;
      const minWidth = 420;
      const minHeight = 320;
      const maxWidth = window.innerWidth - this.viewportPadding;
      const maxHeight = window.innerHeight - this.viewportPadding;
      if (newWidth < minWidth) newWidth = minWidth;
      if (newWidth > maxWidth) newWidth = maxWidth;
      if (newHeight < minHeight) newHeight = minHeight;
      if (newHeight > maxHeight) newHeight = maxHeight;
      this.dialogWidth = newWidth;
      this.dialogHeight = newHeight;
    },

    stopResize() {
      if (!this.resizing) return;
      this.resizing = false;
      window.removeEventListener("mousemove", this.onResize);
      window.removeEventListener("mouseup", this.stopResize);
    },
    showPreview() {
      this.$refs['preview-dialog'].show(this.rules, this.model, ItemKindModel)
          .then((result) => {
            Object.assign(this.model, result);
          })
          .catch((e) => {
            this.$alert("Failed to parse config!" + e.message);
          });
    },
  },
  beforeDestroy() {
    window.removeEventListener("mousemove", this.onResize);
    window.removeEventListener("mouseup", this.stopResize);
  }
};
</script>

<style scoped>
.resizable-card {
  position: relative;
  display: flex;
  flex-direction: column;
}
.resizable-card__body {
  flex: 1;
  overflow: auto;
}
.resizable-card__actions {
  flex-shrink: 0;
}
.resize-handle {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 14px;
  height: 14px;
  cursor: se-resize;
  border-right: 2px solid rgba(0, 0, 0, 0.3);
  border-bottom: 2px solid rgba(0, 0, 0, 0.3);
}
.no-select {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}
</style>